import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { PDFDownloadLink, PDFViewer } from '@react-pdf/renderer';
import api, { endpoints } from '../../services/api';
import PalletLabelPDF from '../../components/PDF/PalletLabelPDF';
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Paper,
  Grid,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Download as DownloadIcon,
  ArrowBack as BackIcon,
  Warning as WarningIcon,
  Straighten as OversizeIcon,
  Visibility as PreviewIcon,
  PictureAsPdf as PdfIcon,
} from '@mui/icons-material';

const PalletLabel = () => {
  const { awb, placa } = useParams();
  const [pallet, setPallet] = useState(null);
  const [vuelo, setVuelo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('preview'); // 'preview' or 'pdf'

  useEffect(() => {
    fetchData();
  }, [awb, placa]);

  const fetchData = async () => {
    try {
      setLoading(true);

      const vueloRes = await api.get(`${endpoints.vuelos.detalle}?awb=${awb}`);
      setVuelo(vueloRes.data.data.vuelo);

      const palletRes = await api.get(`${endpoints.pallets.list}?awb=${awb}`);
      const palletData = palletRes.data.data.pallets.find((p) => p.placa === placa);
      setPallet(palletData);
    } catch (error) {
      console.error('Error al cargar datos:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress size={48} />
      </Box>
    );
  }

  if (!pallet) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h5" gutterBottom>
          Pallet no encontrado
        </Typography>
        <Button component={Link} to={`/vuelos/${awb}/gestionar`} startIcon={<BackIcon />}>
          Volver
        </Button>
      </Box>
    );
  }

  const hasDG = pallet.dg;
  const hasOversize = pallet.xl && pallet.xl !== 'N/A';

  return (
    <Box>
      {/* Controls */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
        <PDFDownloadLink
          document={<PalletLabelPDF pallet={pallet} vuelo={vuelo} />}
          fileName={`etiqueta-${pallet.placa}-${awb}.pdf`}
          style={{ textDecoration: 'none' }}
        >
          {({ loading: pdfLoading }) => (
            <Button
              variant="contained"
              color="success"
              startIcon={pdfLoading ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
              disabled={pdfLoading}
            >
              {pdfLoading ? 'Generando...' : 'Descargar PDF'}
            </Button>
          )}
        </PDFDownloadLink>

        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(e, value) => value && setViewMode(value)}
          size="small"
        >
          <ToggleButton value="preview">
            <PreviewIcon sx={{ mr: 1 }} /> Vista Previa
          </ToggleButton>
          <ToggleButton value="pdf">
            <PdfIcon sx={{ mr: 1 }} /> PDF
          </ToggleButton>
        </ToggleButtonGroup>

        <Button
          component={Link}
          to={`/vuelos/${awb}/gestionar`}
          variant="contained"
          color="inherit"
          startIcon={<BackIcon />}
        >
          Volver
        </Button>
      </Box>

      {/* PDF Viewer */}
      {viewMode === 'pdf' && (
        <Box sx={{ height: 600, border: 1, borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
          <PDFViewer width="100%" height="100%" showToolbar={true}>
            <PalletLabelPDF pallet={pallet} vuelo={vuelo} />
          </PDFViewer>
        </Box>
      )}

      {/* HTML Preview */}
      {viewMode === 'preview' && (
        <Paper
          elevation={3}
          sx={{
            maxWidth: 500,
            mx: 'auto',
            border: 4,
            borderColor: 'common.black',
            overflow: 'hidden',
          }}
        >
          {/* Header with AWB */}
          <Box
            sx={{
              bgcolor: 'common.black',
              color: 'common.white',
              py: 2,
              textAlign: 'center',
            }}
          >
            <Typography variant="h5" fontWeight="bold">
              VUELO: {awb}
            </Typography>
          </Box>

          {/* Main content */}
          <Box sx={{ p: 3 }}>
            {/* Placa - Large */}
            <Box
              sx={{
                textAlign: 'center',
                borderBottom: 2,
                borderColor: 'common.black',
                pb: 3,
                mb: 3,
              }}
            >
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 900,
                  letterSpacing: 4,
                  fontSize: { xs: '2.5rem', sm: '3.5rem' },
                }}
              >
                {pallet.placa}
              </Typography>
            </Box>

            {/* Info Grid */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={6}>
                <Box sx={{ border: 2, borderColor: 'common.black', p: 2 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight="bold">
                    DESTINO
                  </Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {vuelo?.Destino}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box sx={{ border: 2, borderColor: 'common.black', p: 2 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight="bold">
                    PESO TOTAL
                  </Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {pallet.pesoTotalP} KG
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box sx={{ border: 2, borderColor: 'common.black', p: 2 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight="bold">
                    POSICIÓN
                  </Typography>
                  <Typography variant="h5" fontWeight="bold">
                    {pallet.tipoPos}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box sx={{ border: 2, borderColor: 'common.black', p: 2 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight="bold">
                    TOTAL PIEZAS
                  </Typography>
                  <Typography variant="h5" fontWeight="bold">
                    {pallet.total_piezas} PCS
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            {/* Guías */}
            <Box sx={{ border: 2, borderColor: 'common.black', p: 2, mb: 3 }}>
              <Typography variant="caption" color="text.secondary" fontWeight="bold" display="block" mb={1}>
                GUÍAS
              </Typography>
              <Box>
                {pallet.guias?.map((guia, idx) => (
                  <Typography
                    key={idx}
                    variant="body2"
                    sx={{ fontFamily: 'monospace', fontWeight: 500 }}
                  >
                    {guia}
                  </Typography>
                ))}
              </Box>
            </Box>

            {/* Alerts */}
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              {hasDG && (
                <Chip
                  icon={<WarningIcon />}
                  label="DG - DANGEROUS GOODS"
                  color="warning"
                  sx={{
                    fontWeight: 'bold',
                    fontSize: '1rem',
                    py: 3,
                    px: 2,
                    '& .MuiChip-icon': { fontSize: '1.5rem' },
                  }}
                />
              )}
              {hasOversize && (
                <Chip
                  icon={<OversizeIcon />}
                  label={`${pallet.xl} - OVERSIZE`}
                  sx={{
                    fontWeight: 'bold',
                    fontSize: '1rem',
                    py: 3,
                    px: 2,
                    bgcolor: 'warning.dark',
                    color: 'white',
                    '& .MuiChip-icon': { fontSize: '1.5rem', color: 'white' },
                  }}
                />
              )}
            </Box>

            {/* Notas */}
            {pallet.notas2 && (
              <Box sx={{ mt: 3, border: 2, borderColor: 'common.black', p: 2, bgcolor: 'grey.100' }}>
                <Typography variant="caption" color="text.secondary" fontWeight="bold">
                  NOTAS
                </Typography>
                <Typography variant="body2">{pallet.notas2}</Typography>
              </Box>
            )}

            {/* Footer */}
            <Box
              sx={{
                mt: 3,
                pt: 2,
                borderTop: 2,
                borderColor: 'common.black',
                display: 'flex',
                justifyContent: 'space-between',
              }}
            >
              <Typography variant="caption" color="text.secondary">
                <strong>Fecha:</strong> {vuelo?.fecha ? new Date(vuelo.fecha).toLocaleDateString() : '-'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                <strong>Equipo:</strong> {vuelo?.equipo_nombre}
              </Typography>
            </Box>
          </Box>

          {/* Barcode placeholder */}
          <Box
            sx={{
              borderTop: 4,
              borderColor: 'common.black',
              p: 2,
              textAlign: 'center',
              bgcolor: 'grey.100',
            }}
          >
            <Typography sx={{ fontFamily: 'monospace', fontSize: '1.1rem', letterSpacing: 2 }}>
              ||| {pallet.placa} - {awb} |||
            </Typography>
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default PalletLabel;

